import { useMemo, useState, useContext, useEffect, useRef } from 'react'
import { Controller, Control, useWatch, useForm } from 'react-hook-form'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
//import { useAuth, useUser } from '@clerk/react'
import {
  Separator,
  Spinner,
  YStack,
  useToastController,
  DataLoadingDialog,
  ProgressStepper,
  SimpleText,
  useMedia,
  XStack,
  Group,
  H2,
  H3,
  H4,
  QuestionCard,
  XPDatePicker,
  Card,
  H1,
  Button,
  CheckboxInput,
  Text,
  Tabs,
  SizableText,
  H5,
  Paragraph,
  Accordion,
  Image,
  ScrollView,
  CustomCheckboxInput,
  ActiveButton,
  Switch,
  Checkbox,
  Label
} from '@my/ui'
import { Check } from '@tamagui/lucide-icons-2'
import { 
  color, CustomRedirect, LongPollingSvc, TZABrandGuidelines, 
  useAuthedRequestFn, useLink, useNavigate, useAuth, useUser,
  getImageColorStyle, MiscUtils
} from '@my/util'
import assets from '../../assets/images'
import { APP_ENV  } from '@my/config'

import { FinancialReviewHeading, HomeMainHeading } from '../../components/index'
import { SidebarStateContext } from '../../provider'
import { AccordionTrigger, DropdownField, InputField } from '../financial-profile/helpers/Helper'
import { US_STATES_ABBREVIATIONS } from '../financial-profile/states'
import { TZADocumentLinkMap, TZASECDocumentsLinks, TZATermsTextLinks } from '../termsfeed'
import { pickBy } from 'lodash'
import { PricingPage } from './subscription'
import { Platform } from 'react-native'

const FORCE_BILLING = false;
const FORCE_REGISTRATION = FORCE_BILLING || false;

const exampleUserAccount = {
  "Full name": '',
  "Date of Birth": '',
  "Email": '',
  "Mobile number": '',
  "State of Residency": '',
  'Accepted Legal Terms': true,
  'Acknowledged SEC Documents': true,
  'Acknowledged Savvy Disclaimer': true,
  'Accepted Investment Advisory Agreement': true,
  metadata: true,
  subscription: true,
};

type UserProfile = typeof exampleUserAccount;

const UserProfileForm: React.FC<{
  data: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  setChosenPlan: (plan: string) => void;
  selectPlanMode?: boolean
}> = ({data, onSubmit, onCancel, setChosenPlan, selectPlanMode}) => {
  const media = useMedia()
  const [isDatePickerVisible, setDatePickerVisibility] = useState(false)
  const [subscriptionCache, setSubscriptionCache] = useState<undefined | string>();
  const isFirstRender = useRef(true);

  const showDatePicker = () => {
    setDatePickerVisibility(true)
  }

  const hideDatePicker = () => {
    setDatePickerVisibility(false)
  }

  const handleConfirm = (date) => {
    //console.warn('A date has been picked: ', date)
    hideDatePicker()
  }
  const {control, handleSubmit, formState, watch, setValue } = useForm<UserProfile>({
    defaultValues: { 'Subscription Plan Choice': 'premium', ...data },
  });

  const subscriptionChoice = watch("Subscription Plan Choice" as any);

  const toggleInstitutional = () => {
    if (subscriptionChoice !== 'institutional') {
      setSubscriptionCache(subscriptionChoice);
      setValue("Subscription Plan Choice" as any, 'institutional');
    } else {
      setValue("Subscription Plan Choice" as any, subscriptionCache);
    }
  }

  const submitFn = () => handleSubmit(onSubmit);

  

  const [buttonFns, setButtonFns] = useState({
    cancel: {
      disabled: true,
      fn: () => Promise.resolve()
    },
    submit: {
      disabled: true,
      fn: () => Promise.resolve()
    }
  });
  
  const submitDisabled = !formState.isDirty && data.metadata?.stripeCustomerId;
  const cancelDisabled = false; //!data?.saved;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; 
    }
      
    if (!selectPlanMode) {
      setChosenPlan(subscriptionChoice);
    }
  }, [subscriptionChoice]);

  useEffect(() => {
    
    setButtonFns({
      submit: {
        disabled: submitDisabled,
        fn: submitFn()
      },
      cancel: {
        disabled: cancelDisabled,
        fn: () => { onCancel(); return Promise.resolve(); }
      },
    });
  }, [submitDisabled, cancelDisabled]);

  const [accordionIdx, setAccordionIdx] = useState(1);

  const accessParityTab = <YStack gap={10}>
    <Paragraph>How will you access Parity?</Paragraph>
    
    <YStack gap={10} {...(subscriptionChoice === 'institutional' ? {style: {display: 'none'}} : {})}>
      <Paragraph>Choose a plan: Two weeks free. We'll remind you before it ends, and you can downgrade inside the app at any time.</Paragraph>
      <CustomCheckboxInput
        options={[{
            value: 'student',
            label: 'Student - $4.99',
            description: "For making smart money moves on a college budget. Everything in Growth, but priced for cram sessions and ramen nights. (And yes, we actually verify your student status... nice try, though!)"
          }
        ]}
        control={control}
        name="Subscription Plan Choice"
        circle
        optionDescriptions
        title="Student"
      />
      <CustomCheckboxInput
        options={[{
            value: 'growth',
            label: 'Growth - $9.99',
            description: "For getting your footing. Everything you've seen so far, at a steadier pace. Ask Savvy up to 20 times a month. Accounts refresh weekly. Your plan updates monthly."
          }
        ]}
        control={control}
        name="Subscription Plan Choice"
        circle
        optionDescriptions
        title="Growth"
      />
      <CustomCheckboxInput
        options={[
          {
            value: 'premium',
            label: 'Premium - $12.99',
            description: "For when you're moving quickly and want the picture current. Ask Savvy as often as you like. Accounts refresh daily. Your plan updates whenever something changes."
          }
        ]}
        control={control}
        name="Subscription Plan Choice"
        circle
        optionDescriptions
        title="Premium"
      />

    <Paragraph>Investment management is the same on all plans, with the same advisory fee on assets we manage. These plans only change how much you use Parity.</Paragraph>
    </YStack>

    <XStack 
      style={{
        borderRadius: 5, borderWidth: 1, borderColor: 'rgba(0,0,0,.1)', padding: 5, justifyContent: 'center', alignItems: 'center', gap: 5, cursor: 'pointer'
      }}
      onPress={toggleInstitutional}
    >
      <Checkbox bg={subscriptionChoice === 'institutional' ? color.tzaGreen : '#E9ECEF'}
        id={'accesscodecheck'} checked={subscriptionChoice === 'institutional'} onCheckedChange={toggleInstitutional}
      >
        <Checkbox.Indicator>
              <Check color={'#fff'} />
        </Checkbox.Indicator>
      </Checkbox>

      <Label size={'sm'} htmlFor={'accesscodecheck'} opacity={subscriptionChoice !== 'institutional' ? 1 : 1}>
        I have an institutional access code.
      </Label>
    </XStack>
  </YStack>

  const selectPlanUI = <YStack style={{
    maxWidth: 700, 
    width: '100%',
    marginHorizontal: 20,
    gap: 10
  }}>
    <H2>Finish Subscribing</H2>
    <YStack gap={10} style={{padding: media['max-sm'] ? 0 : '0 18px'}}>
      {accessParityTab}
    </YStack>
    <ActiveButton
      style={{width: 230, marginHorizontal: 'auto'}}
      onPress={() => setChosenPlan(subscriptionChoice)}
      ButtonTextProps={{children: 'Next'}}
    />
  </YStack>

  return selectPlanMode
  ? selectPlanUI
  : <Card style={{
    maxWidth: 700, 
    width: '100%',
    borderColor: 'rgb(230,230,230)',
    borderWidth: 1, borderStyle: 'solid'
  }}>
      <Card.Header>
        <H2>We're here to help you figure it out.</H2>
        <H2><H2 color={color.tzaGreen}>Parity</H2> awaits...</H2>
        <H5>Complete your <H5 fontWeight={700}>Account Registration</H5> now:</H5>
      </Card.Header>
      <YStack gap={10} style={{padding: media['max-sm'] ? 0 : '0 18px'}}>
        <Accordion collapsible overflow="hidden" type="single" value={'a'+accordionIdx} onValueChange={(idx) => setAccordionIdx(parseInt(idx[1]))}>
          <Accordion.Item value="a1">
            <AccordionTrigger title={'Welcome to The Zillennial Advisors!'} />
            <Accordion.HeightAnimator transition="medium">
              <Accordion.Content transition="medium" exitStyle={{ opacity: 0 }} style={{backgroundColor: 'white'}}>
                {accessParityTab}
              </Accordion.Content>
            </Accordion.HeightAnimator>
          </Accordion.Item>
          <Accordion.Item value="a2">
            <AccordionTrigger title={'Confirm your Account Information'} />
            <Accordion.HeightAnimator transition="medium">
              <Accordion.Content 
                transition="medium" exitStyle={{ opacity: 0 }} 
                style={{backgroundColor: 'white', gap: 10}}
              >
                <InputField
                  control={control}
                  name="Full name"
                  placeholder="Full Name"
                  icon={assets.profile2}
                  rules={{ required: true, pattern: `^(?!\s*$)[A-Za-z][A-Za-z'\-]+(?:\s+[A-Za-z][A-Za-z'\-]+)*$` }} // Required field validation
                />
                <Controller
                  name="Date of Birth"
                  control={control}
                  rules={{ required: true }} // Required field validation
                  render={({ field: { onChange, name, value }, fieldState: { error } }) => (
                    <>
                      <XPDatePicker
                        onChange={(date) => onChange(date)}
                        name={name}
                        value={value}
                        placeHolder="Date of Birth"

                        icon={assets.AdvisorGender}
                        showIcon={true}
                        tintColor={error ? color.lightRed : 'rgb(133, 193, 157)'}
                        color={color.black}
                        onPress={showDatePicker}
                        borderColor={error ? color.lightRed : color.dullGrey}
                        isVisible={isDatePickerVisible}
                        onConfirm={handleConfirm}
                        onCancel={hideDatePicker}
                        actionButtonProps={{
                          unstyled: true,
                          width: '100%',
                          height: 40,
                          borderWidth: 2,
                          alignItems: 'center',
                          paddingHorizontal: 15,
                          flexDirection: 'row',
                          borderRadius: 4
                        }}
                        simpleTextProps={{color: error ? color.lightRed : color.black}}
                        webNative
                      />
                    </>
                  )}
                />
                <InputField
                  control={control}
                  name="Email"
                  placeholder="Email Address"
                  icon={assets.message}
                  readOnly
                  rules={{ required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/i }} // Email format validation
                />
                <InputField
                  control={control}
                  name="Mobile number"
                  placeholder="Mobile number"
                  icon={assets.AdvisorMobile}
                  rules={{ minLength: 6, maxLength: 18 }} // Length validation for mobile number
                />
                <DropdownField
                  control={control}
                  name="State of Residency"
                  icon={assets.AdvisorLocation}
                  placeholder="U.S. State/Territory of Residence"
                  items={US_STATES_ABBREVIATIONS} // Dropdown options for U.S. states
                  rules={{ required: true }}
                  showLabel
                  native
                />
              </Accordion.Content>
            </Accordion.HeightAnimator>
          </Accordion.Item>
          <Accordion.Item value="a3">
            <AccordionTrigger title={'Accept Documents & Legal Agreements'} />
            <Accordion.HeightAnimator transition="medium">
              <Accordion.Content transition="medium" exitStyle={{ opacity: 0 }} style={{
                backgroundColor: 'white', gap: 20
              }}>
                <YStack style={{marginTop: 10, gap: 10}}>
                  <Paragraph>Please review these important documents:</Paragraph>
                  <Paragraph>📄 {TZADocumentLinkMap.brochure}</Paragraph>
                  <Paragraph>📄 {TZADocumentLinkMap.relationshipSummary}</Paragraph>
                  {CheckboxInput(
                    {control, name: 'Acknowledged SEC Documents', rules: { required: true }},
                    { label: "I have received and reviewed all the documents above." }
                  )}
                </YStack>
                {CheckboxInput(
                  {control, name: 'Accepted Legal Terms', rules: { required: true }},
                  { label: <Text>I have read and agree to the <TZATermsTextLinks />.</Text> }
                )}
                {CheckboxInput(
                  {control, name: 'Accepted Investment Advisory Agreement', rules: { required: true }},
                  { label: <Text>I have read and agree to the full {TZADocumentLinkMap.investmentAdvisory}.</Text> }
                )}
              </Accordion.Content>
            </Accordion.HeightAnimator>
          </Accordion.Item>
        </Accordion>
        
      </YStack>
      <Card.Footer>
        <XStack style={{alignItems: 'center', width: '100%', padding: 18}} gap={5}>
      
          <Group size="$3" orientation="horizontal" gap={5} marginLeft='auto' marginTop={10} borderRadius={9}>
            <Group.Item>
                <Button 
                  disabled={buttonFns.cancel.disabled}
                  {...(!buttonFns.cancel.disabled ? {backgroundColor: '#ff3333', color: 'white', fontWeight: 500, hoverStyle: {backgroundColor: TZABrandGuidelines.colorsMap.secondarySalmon}} : {color: '#ff3333'})}
                  onPress={buttonFns.cancel.fn} size="$3"
                >Cancel</Button>
              </Group.Item>
            {accordionIdx === 3 ? <>
              <Group.Item>
                <Button 
                  disabled={buttonFns.submit.disabled} 
                  {...(!buttonFns.submit.disabled ? {backgroundColor: TZABrandGuidelines.colorsMap.primaryGreen, color: 'white', fontWeight: 500, hoverStyle: {backgroundColor: color.lightGreen}} : {color: TZABrandGuidelines.colorsMap.primaryGreen})}
                  onPress={buttonFns.submit.fn} size="$3"
                >Agree & Continue</Button>
              </Group.Item>
            </>
            : <Group.Item>
              <Button 
                {...({backgroundColor: TZABrandGuidelines.colorsMap.primaryGreen, color: 'white', fontWeight: 500, hoverStyle: {backgroundColor: color.lightGreen}}) }
                onPress={() => setAccordionIdx(accordionIdx+1)} size="$3"
              >Next</Button>
            </Group.Item>}
          </Group>
        </XStack>
      </Card.Footer>
  </Card>
}

type SubscriptionPlan = 'growth' | 'premium' | 'institutional';

export function UserProfileScreen() {
  const { userId, signOut } = useAuth()
  const { user } = useUser()
  const sidebarState = useContext(SidebarStateContext)
  const toast = useToastController()
  const queryClient = useQueryClient()
  const authedRequestFn = useAuthedRequestFn()
  const { sm } = useMedia()
  const navigate = useNavigate();

  const [dataOpTs, setDataOpTs] = useState(0);
  const [formSubmitting, setFormSubmittingCore] = useState(false);
  const [viewState, setViewState] = useState<'registration' | 'billing'>('registration');
  const [chosenPlan, setChosenPlan] = useState<SubscriptionPlan | ''>(FORCE_BILLING ? 'premium' : '');
  const setFormSubmitting = (val: boolean) => {
    setFormSubmittingCore(val);
    setDataOpTs(Date.now());
  }

  //const [pricingTableClientSecret, setPricingTableClientSecret] = useState('');

  const timeSinceDataOp = APP_ENV.TZA_ENV !== 'production'
  ? <SimpleText>{(Date.now() - dataOpTs) / 1000} seconds</SimpleText>
  : null

  const formDataQuery = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => authedRequestFn('/user-profile'),
  }) //'Accepted Legal Terms'

  const financialProfileQuery = useQuery({
    queryKey: ['financial-profile'],
    queryFn: () => authedRequestFn('/financial-profile'),
  })

  const mutation = useMutation({
    mutationFn: (data: any) =>
      authedRequestFn('/user-profile', { method: 'POST', body: data }),
    onMutate: () => {
      setFormSubmitting(true);
      //LongPollingSvc.trigger('financial-review');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
      toast.show('Successfully saved!', {
        //message: 'Your updated Financial Review will be available shortly.',
      })
    },
    onSettled: (data) => {
      setFormSubmitting(false);
      //console.log({data});
      //setPricingTableClientSecret(data.pricingTableClientSecret);
      //@ts-ignore
      //navigate('/');
      setViewState('billing');
    },
  })

  const onSubmit = (data: any) => mutation.mutate(data)
  const onCancel = async () => {
    const shouldSignOut = await MiscUtils.dialog.confirm("Would you like to sign out?");
    if (shouldSignOut) {
      await signOut();
      navigate('/');
    } else {
    }
  }

  const dataIsLoading = formDataQuery.isLoading || financialProfileQuery.isLoading;
  const formData = pickBy({
    ...({
      'Full name': user?.fullName,
      Email: user?.primaryEmailAddress?.emailAddress,
    }),
    ...(financialProfileQuery.data || {}),
    ...(formDataQuery.data || {}),
  }, (_, key) => exampleUserAccount[key] !== undefined) as UserProfile;

  useEffect(() => {
    if (!dataIsLoading && formData?.['Subscription Plan Choice']) {
      setChosenPlan(formData['Subscription Plan Choice'] || '');
    }
    const allItemsTrue = formData?.['Accepted Legal Terms'] && formData?.['Accepted Investment Advisory Agreement'] && formData?.metadata?.['stripeCustomerId'];
    if (!dataIsLoading && allItemsTrue) {
      if (!FORCE_BILLING && formData.subscription?.['productName']) {
        if (Platform.OS === 'web') {
          window.scrollTo(0,0);
        }
        if (!FORCE_REGISTRATION) {
          navigate('/');
        }
        //window['switchToBilling'] = () => setViewState('billing')
      } else {
        setViewState('billing');
      }
    } else {
      if (FORCE_BILLING) {
        setViewState('billing');
      }
    }
  }, [dataIsLoading, financialProfileQuery.data, formDataQuery.data]);

  //console.log({viewState, chosenPlan})

  const content = viewState === 'registration' 
  ? <UserProfileForm data={formData} onSubmit={onSubmit} onCancel={onCancel} setChosenPlan={(plan) => setChosenPlan(plan as any)} />
  : chosenPlan
    ? <PricingPage onCancel={onCancel} chosenPlan={chosenPlan} />
    : <UserProfileForm data={formData} onSubmit={onSubmit} onCancel={onCancel} setChosenPlan={(plan) => setChosenPlan(plan as any)} selectPlanMode={true} /> 

  const output = dataIsLoading 
  ? <XStack style={{flexGrow: 1, height: '100%', width: '100%', justifyContent: 'center', alignItems: 'center'}}><Spinner size="large" color="$green10" /></XStack> 
  : <YStack 
    f={1} 
    bc="$background" 
    p="$4" 
    gap={20} /* $platform-web={{ padding: sm ? 0 : '$4' }} */ 
    bg="#fff"
    style={{
      justifyContent: 'center',
      alignItems: 'center'
    }}
  >
    {content}
    <Separator />
    <DataLoadingDialog open={formSubmitting} onOpenChange={setFormSubmitting}>
      <YStack alignItems='center' gap={15}>
        {/* <H2 color={TZABrandGuidelines.colorsMap.primaryGreen}>Your Financial Profile has been submitted!</H2>
        <H3>Parity is now analyzing and thinking about your data...</H3>
        <SimpleText fontSize={18}>Go grab a cup of coffee ☕ and come back in 1-2 minutes! </SimpleText>
        <SimpleText >(Don't close your browser!)</SimpleText> */}
        <Spinner size="large" color="$green10" />
        {false && timeSinceDataOp}
      </YStack>
    </DataLoadingDialog>
  </YStack>

  return (
    Platform.OS === 'web' ? (
      output
    ) : (
      <ScrollView backgroundColor={color.white}>
        {output}
      </ScrollView>
    )
  );
}
